import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/free-solid-svg-icons";
import { useFighter } from "../../hooks/fighter/useFighter";

const formatHeightInches = (inches) => {
  if (!inches && inches !== 0) return "—";
  const val = Number(inches);
  if (Number.isNaN(val)) return "—";
  const ft = Math.floor(val / 12);
  const inLeft = Math.round(val % 12);
  return `${ft}' ${inLeft}"`;
};

const formatDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString();
};

const StatRow = ({ label, value }) => (
  <div className="flex items-center justify-between py-1.5">
    <span className="text-sm text-gray-600">{label}</span>
    <span className="text-sm font-semibold text-gray-900">{value}</span>
  </div>
);

const FighterInfoModal = ({ fighterId, onClose }) => {
  const { data, isLoading, isError, error } = useFighter(fighterId);

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center p-3">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />

      <div className="relative w-full max-w-md rounded-2xl shadow-2xl overflow-hidden bg-white">
        {/* Header */}
        <div className="bg-gradient-to-r from-deep-red to-bright-red text-white p-4 flex justify-between items-center">
          <div className="min-w-0">
            <h3 className="text-lg font-bold truncate">
              {isLoading
                ? "Loading…"
                : `${data?.FirstName ?? ""} ${data?.LastName ?? ""}`.trim() ||
                  "Fighter"}
            </h3>
            <p className="text-white/80 text-sm">
              {isLoading
                ? ""
                : data?.Nickname
                ? `“${data.Nickname}”`
                : data?.WeightClass || ""}
            </p>
          </div>
          <button
            onClick={onClose}
            className="ml-3 w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors focus:outline-none focus:ring-2 focus:ring-white/80"
            aria-label="Close fighter info"
          >
            <FontAwesomeIcon icon={faXmark} className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-4">
          {isLoading && (
            <div className="animate-pulse space-y-3">
              <div className="h-4 bg-gray-200 rounded" />
              <div className="h-4 bg-gray-200 rounded" />
              <div className="h-4 bg-gray-200 rounded" />
            </div>
          )}

          {isError && (
            <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-3 text-sm">
              {error?.response?.status === 404
                ? "Fighter not found in database."
                : "Failed to load fighter details."}
            </div>
          )}

          {!isLoading && !isError && data && (
            <div className="space-y-4">
              {/* Record */}
              <div className="bg-gray-50 border border-gray-200 rounded-xl p-3">
                <div className="text-xs text-gray-500 font-medium mb-1">
                  Record
                </div>
                <div className="text-2xl font-extrabold text-gray-900">
                  {data.Wins ?? 0}-{data.Losses ?? 0}
                  {Number(data.Draws) ? `-${data.Draws}` : ""}
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  Titles: {data.TitleWins ?? 0}W / {data.TitleLosses ?? 0}L /{" "}
                  {data.TitleDraws ?? 0}D
                </div>
              </div>

              {/* Stats grid */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-white border border-gray-200 rounded-xl p-3">
                  <StatRow
                    label="Weight Class"
                    value={data.WeightClass || "—"}
                  />
                  <StatRow
                    label="Height"
                    value={formatHeightInches(data.Height)}
                  />
                  <StatRow
                    label="Weight"
                    value={data.Weight ? `${data.Weight} lb` : "—"}
                  />
                  <StatRow
                    label="Reach"
                    value={data.Reach ? `${data.Reach} in` : "—"}
                  />
                </div>
                <div className="bg-white border border-gray-200 rounded-xl p-3">
                  <StatRow
                    label="TKO Wins"
                    value={data.TechnicalKnockouts ?? 0}
                  />
                  <StatRow label="Sub Wins" value={data.Submissions ?? 0} />
                  <StatRow
                    label="TKO Losses"
                    value={data.TechnicalKnockoutLosses ?? 0}
                  />
                  <StatRow
                    label="Sub Losses"
                    value={data.SubmissionLosses ?? 0}
                  />
                </div>
              </div>

              {/* Meta */}
              <div className="bg-white border border-gray-200 rounded-xl p-3">
                <StatRow label="DOB" value={formatDate(data.BirthDate)} />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FighterInfoModal;
